"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Search, SlidersHorizontal, X } from "lucide-react";
import type { CoursePage } from "@/lib/courses";
import { cn } from "@/lib/utils";
import { Pagination } from "@/components/portal/Pagination";
import { CourseGrid } from "./CourseGrid";
import { Select } from "@/components/ui/Select";

const levels = ["Beginner", "Intermediate", "Advanced"] as const;

/**
 * The URL is the source of truth: every filter, the search text and the page live in the query string,
 * and the server (courses/page.tsx → /api/courses/browse) returns exactly the matching slice.
 */
export function CoursesExplorer({ data }: { data: CoursePage }) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [pending, startTransition] = useTransition();
  const topRef = useRef<HTMLDivElement>(null);

  const category = params.get("category") ?? "";
  const level = params.get("level") ?? "";
  const mode = params.get("mode") ?? "";
  const urlQuery = params.get("q") ?? "";
  const [query, setQuery] = useState(urlQuery);

  // keep the box in step when the URL changes from outside (Back button, Clear)
  useEffect(() => setQuery(urlQuery), [urlQuery]);

  function update(changes: Record<string, string>, { keepPage = false } = {}) {
    const next = new URLSearchParams(params.toString());
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!keepPage) next.delete("page"); // a new filter always starts from page 1
    const qs = next.toString();
    startTransition(() => router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false }));
  }

  // search runs on the server, a moment after typing stops
  useEffect(() => {
    if (query.trim() === urlQuery) return;
    const t = setTimeout(() => update({ q: query.trim() }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- update() is rebuilt each render; only the typed text should retrigger
  }, [query]);

  function goToPage(p: number) {
    update({ page: p > 1 ? String(p) : "" }, { keepPage: true });
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function clearAll() {
    setQuery("");
    startTransition(() => router.replace(pathname, { scroll: false }));
  }

  const activeCount = [category, level, mode, urlQuery].filter(Boolean).length;
  const modeOptions = data.modes.includes(mode) || !mode ? data.modes : [...data.modes, mode];

  return (
    <div ref={topRef} className="scroll-mt-28">
      {/* Toolbar */}
      <div className="rounded-[var(--radius-card)] border border-line bg-white p-4 shadow-soft sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search courses, topics…"
              aria-label="Search courses"
              className="h-11 w-full rounded-xl border border-line bg-cream/50 pl-10 pr-3 text-sm text-ink outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-600/15"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Level"
              value={level}
              onChange={(v) => update({ level: v })}
              options={[{ value: "", label: "All Levels" }, ...levels.map((l) => ({ value: l, label: l }))]}
            />
            <FilterSelect
              label="Mode"
              value={mode}
              onChange={(v) => update({ mode: v })}
              options={[{ value: "", label: "Any Mode" }, ...modeOptions.map((m) => ({ value: m, label: m }))]}
            />
          </div>
        </div>

        {/* Category chips */}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4">
          <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Category
          </span>
          {["", ...data.categories].map((c) => (
            <button
              key={c || "all"}
              onClick={() => update({ category: c })}
              aria-pressed={category === c}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-sm font-medium transition-all",
                category === c
                  ? "bg-brand-600 text-white shadow-soft"
                  : "border border-line bg-white text-ink-soft hover:border-brand-500/50 hover:text-brand-700"
              )}
            >
              {c || "All"}
            </button>
          ))}
          {activeCount > 0 && (
            <button
              onClick={clearAll}
              className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-600/10"
            >
              <X className="h-3.5 w-3.5" /> Clear ({activeCount})
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <p className="mt-6 text-sm text-ink-muted" aria-live="polite">
        Showing <span className="font-semibold text-ink">{data.total}</span> {data.total === 1 ? "course" : "courses"}
      </p>

      <div className={cn("transition-opacity", pending && "opacity-50")} aria-busy={pending}>
        {data.courses.length > 0 ? (
          <>
            <CourseGrid
              key={`${category}|${level}|${mode}|${urlQuery}|${data.page}`}
              courses={data.courses}
              columns={3}
              className="mt-6"
              immediate
            />
            {data.pageCount > 1 && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                shown={data.courses.length}
                onPage={goToPage}
                className="mt-10"
              />
            )}
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-[var(--radius-card)] border border-dashed border-line bg-white/60 px-6 py-16 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600/8 text-brand-600">
              <Search className="h-7 w-7" />
            </div>
            <h3 className="mt-4 font-display text-xl font-semibold text-ink">No courses found</h3>
            <p className="mt-1 max-w-sm text-sm text-ink-soft">
              We couldn’t find a match. Try a different keyword or clear your filters.
            </p>
            <button
              onClick={clearAll}
              className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <X className="h-4 w-4" /> Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="text-ink-muted">{label}</span>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-lg border border-line bg-cream/50 px-3 text-sm font-medium text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-600/15"
      >
        {options.map((o) => (
          <option key={o.label} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </label>
  );
}
